/**
 * Minimal typing for the parts of Google Publisher Tag this codebase uses,
 * plus a `cmd` helper that is safe before gpt.js has loaded.
 */
export interface GptSafeFrameConfig {
  allowOverlayExpansion?: boolean;
  allowPushExpansion?: boolean;
  sandbox?: boolean;
}

export interface GptSlot {
  addService(service: unknown): GptSlot;
  defineSizeMapping(mapping: unknown[][]): GptSlot;
  setSafeFrameConfig(cfg: GptSafeFrameConfig): GptSlot;
  setTargeting(key: string, value: string | string[]): GptSlot;
  getSlotElementId(): string;
  getAdUnitPath(): string;
}

export interface GptRenderEndedEvent {
  slot: GptSlot;
  isEmpty: boolean;
  isBackfill?: boolean;
  lineItemId?: number | null;
  size?: number[] | string | null;
}

export interface GptSizeMappingBuilder {
  addSize(viewport: [number, number], sizes: unknown[]): GptSizeMappingBuilder;
  build(): unknown[][] | null;
}

export interface GptPubAds {
  enableSingleRequest(): void;
  enableLazyLoad(cfg: { fetchMarginPercent?: number; renderMarginPercent?: number; mobileScaling?: number }): void;
  collapseEmptyDivs(collapse?: boolean, before?: boolean): void;
  setCentering(v: boolean): void;
  setSafeFrameConfig(cfg: GptSafeFrameConfig): void;
  setPrivacySettings(v: Record<string, unknown>): void;
  setTargeting(key: string, value: string | string[]): void;
  clearTargeting(key?: string): void;
  getTargeting(key: string): string[];
  refresh(slots?: GptSlot[], opts?: { changeCorrelator?: boolean }): void;
  addEventListener(name: string, cb: (e: GptRenderEndedEvent) => void): void;
  removeEventListener(name: string, cb: (e: GptRenderEndedEvent) => void): void;
  getSlots(): GptSlot[];
}

export interface GoogleTag {
  cmd: Array<() => void>;
  apiReady?: boolean;
  pubads(): GptPubAds;
  defineSlot(path: string, sizes: unknown[], div: string): GptSlot | null;
  defineOutOfPageSlot(path: string, div: string): GptSlot | null;
  display(div: string): void;
  destroySlots(slots?: GptSlot[]): boolean;
  enableServices(): void;
  sizeMapping(): GptSizeMappingBuilder;
  openConsole(): void;
}

declare global {
  interface Window {
    googletag?: GoogleTag;
  }
}

export const GPT_SRC = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";

/** Queue a callback on googletag.cmd, creating the stub if gpt.js is not loaded yet. */
export function gpt(fn: (g: GoogleTag) => void): void {
  if (typeof window === "undefined") return;
  const w = window as Window;
  if (!w.googletag) w.googletag = { cmd: [] } as unknown as GoogleTag;
  w.googletag.cmd.push(() => fn(w.googletag as GoogleTag));
}
